import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Check, ShieldCheck, Sparkles } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../assets/images/logo.png';
import { useAuthStore } from '../stores/authStore';
import { SubscriptionService } from '../services/subscriptionService';
import {
  buildSubscriptionRequestRecord,
  getCurrentPlan,
  getPendingSubscriptionRequest,
  getPlanPrice,
  getUserSubscription,
  LAUNCH_DISCOUNT_LIMIT,
  PLAN_IDS,
  SUBSCRIPTION_PAGE_HEADER,
  SUBSCRIPTION_PLANS,
  SUBSCRIPTION_THEME,
} from '../utils/subscription';

const PLAN_FEATURES = {
  [PLAN_IDS.FREE]: [
    '30 days to explore classroom drafting',
    'Save up to 10 documents',
    'Up to 5 pages per document',
    'Upgrade to Basic or Pro anytime',
  ],
  [PLAN_IDS.BASIC]: [
    'Save tool canvases and slides',
    'Unlimited documents with up to 20 pages each',
    'PDF uploads',
    'Sharing',
  ],
  [PLAN_IDS.PRO]: [
    'Everything in Basic',
    'Unlimited documents with up to 100 pages each',
    'Full assessment module',
    'Manual eMarking and analysis',
    'Learner management',
  ],
};

const planOrder = [PLAN_IDS.FREE, PLAN_IDS.BASIC, PLAN_IDS.PRO];

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    maximumFractionDigits: 0,
  }).format(amount);

const PricingPage = () => {
  const navigate = useNavigate();
  const { user, userData, updateUserProfile } = useAuthStore();
  const currentSubscription = getUserSubscription(userData);
  const currentPlan = getCurrentPlan(userData);
  const pendingSubscriptionRequest = getPendingSubscriptionRequest(userData);
  const [launchSubscriberCount, setLaunchSubscriberCount] = useState(0);
  const [isLoadingCount, setIsLoadingCount] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  useEffect(() => {
    let isMounted = true;

    SubscriptionService.getLaunchSubscriberCount()
      .then((count) => {
        if (isMounted) setLaunchSubscriberCount(count);
      })
      .catch((error) => {
        console.error('Failed to load launch subscriber count:', error);
      })
      .finally(() => {
        if (isMounted) setIsLoadingCount(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const launchDiscountEligible = launchSubscriberCount < LAUNCH_DISCOUNT_LIMIT;
  const launchSpotsLeft = Math.max(LAUNCH_DISCOUNT_LIMIT - launchSubscriberCount, 0);
  const selectedPlan = selectedPlanId ? SUBSCRIPTION_PLANS[selectedPlanId] : null;
  const hasPendingPaidRequest = Boolean(pendingSubscriptionRequest);

  const checkoutSummary = useMemo(() => {
    if (!selectedPlan || selectedPlan.id === PLAN_IDS.FREE) return null;
    const discounted = getPlanPrice(selectedPlan.id, { withLaunchDiscount: launchDiscountEligible });
    return {
      listPrice: selectedPlan.annualPrice,
      finalPrice: discounted,
      launchDiscountEligible,
    };
  }, [launchDiscountEligible, selectedPlan]);

  const handleChoosePlan = (planId) => {
    setCheckoutError('');

    if (!user) {
      navigate('/login', { state: { from: { pathname: '/pricing' } } });
      return;
    }

    if (planId === PLAN_IDS.FREE) {
      return;
    }

    setSelectedPlanId(planId);
  };

  const handleConfirmCheckout = async () => {
    if (!user || !selectedPlan || selectedPlan.id === PLAN_IDS.FREE) return;

    try {
      setIsSubmitting(true);
      setCheckoutError('');
      await updateUserProfile({
        subscriptionRequest: buildSubscriptionRequestRecord({
          planId: selectedPlan.id,
          launchDiscountApplied: launchDiscountEligible,
        }),
      });

      setSelectedPlanId(null);
      navigate('/pricing');
    } catch (error) {
      console.error('Failed to submit subscription request:', error);
      setCheckoutError(error.message || 'Failed to submit subscription request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={SUBSCRIPTION_THEME.pageShell}>
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        <div className={SUBSCRIPTION_THEME.heroShell}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className={SUBSCRIPTION_THEME.backButton}
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
              <div className="mt-6 flex items-center gap-3">
                <div className="rounded-2xl p-2 shadow-lg shadow-sigma-950/20">
                  <img src={logo} alt="Sigma Classroom" className="h-16 w-auto" />
                </div>
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sigma-100/90">
                    {SUBSCRIPTION_PAGE_HEADER.brand}
                  </p>
                  <p className="text-sm text-white/80">Subscription plans for classrooms and schools</p>
                </div>
              </div>
              <div className={SUBSCRIPTION_THEME.eyebrow}>
                <Sparkles className="h-3.5 w-3.5" />
                {SUBSCRIPTION_PAGE_HEADER.eyebrow}
              </div>
              <h1 className="mt-4 max-w-3xl text-4xl font-black tracking-tight text-white sm:text-5xl">
                {SUBSCRIPTION_PAGE_HEADER.title}
              </h1>
              <p className={SUBSCRIPTION_THEME.heroDescription}>
                {SUBSCRIPTION_PAGE_HEADER.description}
              </p>
            </div>

            <div className={SUBSCRIPTION_THEME.offerCard}>
              <div className={SUBSCRIPTION_THEME.offerLabel}>
                <ShieldCheck className="h-4 w-4" />
                {SUBSCRIPTION_PAGE_HEADER.launchOfferTitle}
              </div>
              <p className={SUBSCRIPTION_THEME.offerHighlight}>{SUBSCRIPTION_PAGE_HEADER.launchOfferHighlight}</p>
              <p className={SUBSCRIPTION_THEME.offerDescription}>
                {SUBSCRIPTION_PAGE_HEADER.launchOfferDescription}
              </p>
              <p className={SUBSCRIPTION_THEME.offerStatus}>
                {isLoadingCount
                  ? 'Checking launch spots...'
                  : launchDiscountEligible
                    ? `${launchSpotsLeft} discounted spot${launchSpotsLeft === 1 ? '' : 's'} remaining`
                    : 'Launch discount fully claimed'}
              </p>
            </div>
          </div>

          {checkoutError ? (
            <div className={SUBSCRIPTION_THEME.errorBanner}>
              {checkoutError}
            </div>
          ) : null}

          {pendingSubscriptionRequest ? (
            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Your {SUBSCRIPTION_PLANS[pendingSubscriptionRequest.planId]?.name || 'paid'} plan request is pending admin approval.
            </div>
          ) : null}

          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {planOrder.map((planId) => {
              const plan = SUBSCRIPTION_PLANS[planId];
              const isCurrent = currentPlan.id === plan.id && currentSubscription.status === 'active';
              const isPendingRequest = pendingSubscriptionRequest?.planId === plan.id;
              const discountedPrice = getPlanPrice(plan.id, { withLaunchDiscount: launchDiscountEligible });
              const isPaidPlan = plan.annualPrice > 0;
              const isTrialUnavailable = plan.id === PLAN_IDS.FREE && Boolean(user);

              return (
                <article
                  key={plan.id}
                  className={[
                    SUBSCRIPTION_THEME.planCardBase,
                    SUBSCRIPTION_THEME.planCardAccent[plan.id],
                  ].join(' ')}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-950">{plan.name}</h2>
                      <p className="mt-1 text-sm text-slate-600">
                        {plan.id === PLAN_IDS.FREE
                          ? 'For trying Sigma Classroom before upgrading'
                          : plan.id === PLAN_IDS.BASIC
                            ? 'For teachers saving daily classroom work'
                            : 'For schools running full assessment workflows'}
                      </p>
                    </div>
                    {isCurrent ? (
                      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">
                        Current
                      </span>
                    ) : isPendingRequest ? (
                      <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">
                        Pending Approval
                      </span>
                    ) : null}
                  </div>

                  <div className="mt-8">
                    {isPaidPlan ? (
                      <>
                        <div className="flex items-end gap-3">
                          <span className="text-4xl font-black text-slate-950">{formatCurrency(discountedPrice)}</span>
                          <span className="pb-1 text-sm text-slate-500">per year</span>
                        </div>
                        <div className="mt-2 text-sm text-slate-500">{plan.monthlyLabel} annual plan</div>
                        {launchDiscountEligible ? (
                          <div className="mt-2 text-sm text-amber-700">
                            Launch price from {formatCurrency(plan.annualPrice)} to {formatCurrency(discountedPrice)}
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <div className="text-4xl font-black text-slate-950">30 days</div>
                        <div className="mt-2 text-sm text-slate-500">Included with every account</div>
                      </>
                    )}
                  </div>

                  <div className="mt-8 flex-1 space-y-3">
                    {PLAN_FEATURES[plan.id].map((feature) => (
                      <div key={feature} className="flex items-start gap-3 text-sm leading-6 text-slate-700">
                        <Check className={`mt-1 h-4 w-4 shrink-0 ${SUBSCRIPTION_THEME.featureIcon}`} />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => handleChoosePlan(plan.id)}
                    disabled={isTrialUnavailable || isCurrent || isSubmitting || (isPaidPlan && hasPendingPaidRequest)}
                    className={[
                      'mt-8 inline-flex items-center justify-center rounded-2xl px-4 py-3 text-sm font-semibold transition',
                      isTrialUnavailable || isCurrent || isSubmitting || (isPaidPlan && hasPendingPaidRequest)
                        ? SUBSCRIPTION_THEME.planButton.disabled
                        : SUBSCRIPTION_THEME.planButton[plan.id],
                    ].join(' ')}
                  >
                    {plan.id === PLAN_IDS.FREE
                      ? !user
                        ? 'Sign in for Trial'
                        : currentSubscription.status === 'expired'
                        ? 'Trial Ended'
                        : 'Trial Included'
                      : isPendingRequest
                        ? 'Approval Pending'
                      : user
                        ? `Request ${plan.name}`
                        : `Sign in for ${plan.name}`}
                  </button>
                </article>
              );
            })}
          </div>

          <div className={SUBSCRIPTION_THEME.utilityCard}>
            Trial access lasts 30 days. Paid plans create a subscription request first, and an admin must approve the request before Basic or Pro access is assigned.
          </div>

          <div className={SUBSCRIPTION_THEME.utilityLinks}>
            Need to compare features in context? Open the <Link to="/tools" className={SUBSCRIPTION_THEME.utilityLink}>Tools</Link> page or return to the <Link to="/document/new" className={SUBSCRIPTION_THEME.utilityLink}>document workspace</Link>.
          </div>
        </div>
      </div>

      {selectedPlan && checkoutSummary ? (
        <div className={SUBSCRIPTION_THEME.checkoutOverlay}>
          <div className={SUBSCRIPTION_THEME.checkoutCard}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className={SUBSCRIPTION_THEME.checkoutEyebrow}>Checkout</p>
                <h2 className="mt-2 text-2xl font-bold text-slate-950">{selectedPlan.name} yearly plan</h2>
              </div>
              <button
                type="button"
                onClick={() => setSelectedPlanId(null)}
                className={SUBSCRIPTION_THEME.checkoutClose}
              >
                Close
              </button>
            </div>

            <div className={SUBSCRIPTION_THEME.checkoutSummary}>
              <div className={SUBSCRIPTION_THEME.checkoutRow}>
                <span>List price</span>
                <span>{formatCurrency(checkoutSummary.listPrice)}</span>
              </div>
              <div className={`mt-3 ${SUBSCRIPTION_THEME.checkoutRow}`}>
                <span>Launch discount</span>
                <span>{checkoutSummary.launchDiscountEligible ? '-50%' : '0%'}</span>
              </div>
              <div className="mt-4 border-t border-sigma-100 pt-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-950">Total due today</span>
                  <span className="text-3xl font-black text-slate-950">{formatCurrency(checkoutSummary.finalPrice)}</span>
                </div>
              </div>
            </div>

            <p className="mt-5 text-sm leading-6 text-slate-600">
              This submits a paid-plan request for <span className="font-semibold text-slate-950">{user?.email}</span>. Trial access stays available until it ends or an admin approves the paid plan.
            </p>

            <button
              type="button"
              onClick={handleConfirmCheckout}
              disabled={isSubmitting}
              className={SUBSCRIPTION_THEME.checkoutPrimary}
            >
              {isSubmitting ? 'Submitting request...' : `Request ${selectedPlan.name}`}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PricingPage;
